import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'

interface Props {
  /** 何の説明かを支援技術に伝えるための項目名 */
  label: string
  text: string
}

/** 吹き出しとアイコンの間隔 */
const GAP = 6
/** 画面端にこれ以上寄せない */
const MARGIN = 8
/** マウスがアイコンから吹き出しへ渡る間に閉じないための猶予 */
const CLOSE_DELAY_MS = 150

/**
 * 項目名の横に出す ⓘ アイコンと、その説明の吹き出し。
 *
 * 吹き出しを position: fixed で出すのは、判定カード (.verdict) が角丸のために
 * overflow: hidden を持っており、絶対配置だとカードの外に出た部分が切れるため。
 * fixed の要素は祖先の overflow に切られない。位置は開いた時点で実測する。
 *
 * 開き方は入力手段ごとに分けている。マウスはホバー、キーボードはフォーカスと
 * Enter / Space、ホバーを持たないタッチとペンはタップで開閉する。マウスの
 * クリックを開閉に使うと、重ねて開いた状態から押したときに閉じてしまう。
 */
export function InfoTip({ label, text }: Props) {
  const id = useId()
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const bubbleRef = useRef<HTMLSpanElement>(null)
  const closeTimer = useRef<number | null>(null)

  const cancelClose = () => {
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }

  const show = () => {
    cancelClose()
    setOpen(true)
  }

  const hide = () => {
    cancelClose()
    setOpen(false)
  }

  /**
   * すぐには閉じない。吹き出しの上にマウスを移動できないと、
   * 中の文字を読み返すことも選択することもできなくなる (WCAG 2.1 1.4.13)。
   */
  const hideSoon = () => {
    cancelClose()
    closeTimer.current = window.setTimeout(() => {
      closeTimer.current = null
      setOpen(false)
    }, CLOSE_DELAY_MS)
  }

  const toggle = () => (open ? hide() : show())

  useEffect(() => cancelClose, [])

  useLayoutEffect(() => {
    if (!open) {
      setPos(null)
      return
    }

    const place = () => {
      const button = buttonRef.current
      const bubble = bubbleRef.current
      if (!button || !bubble) return
      const anchor = button.getBoundingClientRect()
      const box = bubble.getBoundingClientRect()

      // アイコンの中央に揃えたうえで、画面の左右からはみ出す分を戻す。
      const centered = anchor.left + anchor.width / 2 - box.width / 2
      const maxLeft = window.innerWidth - box.width - MARGIN
      const left = Math.min(Math.max(MARGIN, centered), Math.max(MARGIN, maxLeft))

      // 下に収まらなければ上に出す。
      const below = anchor.bottom + GAP
      const fitsBelow = below + box.height + MARGIN <= window.innerHeight
      const top = fitsBelow ? below : Math.max(MARGIN, anchor.top - box.height - GAP)

      setPos({ top, left })
    }

    place()

    // fixed で出しているので、スクロールするとアイコンから離れてしまう。
    const close = () => setOpen(false)
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    return () => {
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
    }
  }, [open, text])

  return (
    <span
      className="infotip"
      // ホバーの開閉はマウスだけに任せる。タッチは互換のマウスイベントも投げるので、
      // onMouseEnter で開くとタップで閉じた直後に開き直してしまう。
      onPointerEnter={(event) => {
        if (event.pointerType === 'mouse') show()
      }}
      onPointerLeave={(event) => {
        if (event.pointerType === 'mouse') hideSoon()
      }}
    >
      <button
        ref={buttonRef}
        type="button"
        className="infotip-button"
        aria-label={`${label}の説明`}
        aria-describedby={open ? id : undefined}
        data-open={open || undefined}
        onFocus={show}
        onBlur={hide}
        onPointerDown={(event) => {
          // ホバーのない入力手段はタップで開閉する。マウスはホバーが担当するので触らない。
          if (event.pointerType !== 'mouse') toggle()
        }}
        onClick={(event) => {
          // キーボードの Enter / Space から出たクリックだけを拾う。
          if (event.detail === 0) toggle()
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') hide()
        }}
      >
        i
      </button>
      {open && (
        <span
          ref={bubbleRef}
          id={id}
          role="tooltip"
          className="infotip-bubble"
          style={{
            top: pos?.top ?? 0,
            left: pos?.left ?? 0,
            // 位置を実測するまでの 1 フレームだけ見えないようにする。
            visibility: pos ? 'visible' : 'hidden',
          }}
        >
          {text}
        </span>
      )}
    </span>
  )
}
